/**
 * buildReplaceOrder — single source of truth for building router replace-order payloads.
 *
 * Every modification path (chart drag, chart modal, panel modal, OTO drag)
 * calls this function instead of inlining its own payload construction.
 *
 * @param {Object} order      — current order from orderStore (always up-to-date after Phase 1)
 * @param {Object} changes    — { price?, quantity?, otoUpdates?: [{ otoId, newPrice }] }
 * @param {string} translatedSymbol — WINFUT→WING26 translated symbol for router
 * @returns {Object} router-ready HubOrder payload
 */
import { normalizePrice } from "./priceUtils.js";

export function buildReplaceOrder(order, changes, translatedSymbol) {
  if (!order.originalRouterOrder) {
    throw new Error("Original router order data not available");
  }

  // 1. Spread originalRouterOrder as base (kept current by Phase 1)
  const result = {
    ...order.originalRouterOrder,
  };

  // 2. Override symbol with the translated contract — but NEVER clobber the
  //    broker-assigned concrete series with an unresolved FUT/FUL alias: that would
  //    make the router-boundary freshness gate throw and block a legit modify of an
  //    existing order. If resolution didn't yield a concrete series, keep the
  //    originalRouterOrder.symbol already spread above. (#351 review)
  if (
    translatedSymbol &&
    !translatedSymbol.endsWith("FUT") &&
    !translatedSymbol.endsWith("FUL")
  ) {
    result.symbol = translatedSymbol;
  }

  // 3. Quantity — plain value (no {old,new} wrapper)
  result.quantity = changes.quantity ?? order.quantity;

  // 4. Price — set limit/stop based on order type
  const newPrice = changes.price;
  if (newPrice !== undefined) {
    if (order.oco) {
      // OCO: set the changed field's price. For the other field, only include it
      // if it actually has a value (individual OCO legs like -L/-S only have one price).
      if (order.orderType === "LIMIT") {
        result.limit = newPrice;
        const stopValue = order.stop || order.originalRouterOrder.stop;
        if (stopValue) {
          result.stop = stopValue;
        } else {
          delete result.stop;
        }
      } else if (order.orderType === "STOPLIMIT") {
        result.stop = newPrice;
        const limitValue = order.limit || order.originalRouterOrder.limit;
        if (limitValue) {
          result.limit = limitValue;
        } else {
          delete result.limit;
        }
      }
    } else {
      // Regular orders — set the relevant price field
      if (order.orderType === "LIMIT") {
        result.limit = newPrice;
      } else if (order.orderType === "STOPLIMIT") {
        result.stop = newPrice;
      }
    }
  }

  // 5. OTO legs
  if (order.oto && order.oto.length > 0) {
    const effectiveQuantity = changes.quantity ?? order.quantity;
    const otoSide = order.side === "buy" ? "sell" : "buy";

    result.oto = order.oto.map((leg) => {
      const update = changes.otoUpdates?.find((u) => u.otoId === leg.id);
      const legPrice = update
        ? normalizePrice(translatedSymbol, update.newPrice)
        : leg.price;

      return {
        id: leg.id,
        action: leg.side || leg.action || otoSide,
        quantity: effectiveQuantity,
        limit: leg.orderType === "LIMIT" ? legPrice : undefined,
        stop: leg.orderType === "STOPLIMIT" ? legPrice : undefined,
        symbol: leg.symbol || result.symbol,
      };
    });
  } else {
    // No OTO legs — delete any stale oto from originalRouterOrder spread
    delete result.oto;
  }

  // 6. Clean frontend-only fields that shouldn't go to router
  delete result.isRouterOrder;
  delete result.fromReport;
  delete result.state;
  delete result.orderType;

  // 7. Mark as already normalized (prevents double normalization in replaceOrder)
  result._normalized = true;

  return result;
}
